import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Preview as PreviewIcon,
  Smartphone as SmartphoneIcon,
  TextSnippet as TextSnippetIcon,
  Language as LanguageIcon,
  CalendarToday as CalendarIcon,
  VideoLibrary as VideoIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { debounce } from "lodash";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import TemplateStatusChip from "../../widgets/TemplateStatusChip";

const RcsJioTemplates = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
    }, 500),
    []
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const { data, isLoading, isError, error } = useApiEp(
    `/rcs/get-templates?sq=${searchQuery}`
  );
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Clear debounce on unmount
  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const templates = data || [];

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, minHeight: "100vh", bgcolor: "grey.50" }}>
      <BackBtn />

      {/* Header with Search */}
      <Box sx={{ mb: 2 }}>
        <PageTitle mb={1}>RCS Templates</PageTitle>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search templates by name..."
          value={searchInput}
          onChange={handleSearchChange}
          sx={{
            maxWidth: 400,
            bgcolor: "white",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <TemplatesList
        isMobile={isMobile}
        templates={templates}
        selectedTemplate={selectedTemplate}
        isLoading={isLoading}
        isError={isError}
        error={error}
        searchQuery={searchQuery}
        setSelectedTemplate={setSelectedTemplate}
      />
    </Box>
  );
};
const TemplatesList = ({
  isMobile,
  templates,
  selectedTemplate,
  isLoading,
  error,
  isError,
  searchQuery,
  setSelectedTemplate,
}) => {
  if (isLoading) {
    return <TemplateListSkeleton />;
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading templates: {error?.message || "Unknown error"}
      </Alert>
    );
  }
  return (
    <Grid container spacing={3}>
      {/* Templates List */}
      <Grid item xs={12} md={4}>
        <Paper
          elevation={2}
          sx={{
            height: isMobile ? "400px" : "80vh",
            maxHeight: isMobile ? "400px" : "80vh",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2, bgcolor: "primary.main", color: "white" }}>
            <Typography variant="h6" component="h2">
              Templates ({templates.length})
              {searchQuery && (
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  Search results for "{searchQuery}"
                </Typography>
              )}
            </Typography>
          </Box>

          {templates.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {searchQuery
                  ? "No templates found matching your search."
                  : "No templates available."}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, flex: 1, overflow: "auto" }}>
              {templates.map((template, index) => (
                <React.Fragment key={template._id}>
                  <ListItem
                    button
                    selected={selectedTemplate?._id === template._id}
                    onClick={() => setSelectedTemplate(template)}
                    sx={{
                      transition: "all 0.2s",
                      "&:hover": { bgcolor: "action.hover" },
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        "&:hover": { bgcolor: "primary.light" },
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: "secondary.main" }}>
                        <TextSnippetIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="medium">
                          {template.name || template.templateName}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={(template.language || "").toUpperCase()}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1, mb: 0.5 }}
                          />
                          <Chip
                            label={template.status || "pending"}
                            size="small"
                            color={
                              template.status === "approved"
                                ? "success"
                                : "secondary"
                            }
                            variant="outlined"
                          />
                        </Box>
                      }
                    />
                    <Tooltip title="Preview">
                      <IconButton edge="end" size="small">
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  {index < templates.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Grid>

      {/* Preview Panel */}
      <Grid item xs={12} md={8}>
        {selectedTemplate ? (
          <RcsTemplatePreview template={selectedTemplate} />
        ) : (
          <Paper
            elevation={2}
            sx={{
              height: 400,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
            }}
          >
            <SmartphoneIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
            <Typography variant="h6" color="grey.500">
              {templates.length === 0
                ? "No templates to preview"
                : "Select a template to preview"}
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export const RcsTemplatePreview = ({ template }) => {
  const theme = useTheme();

  // Helper function to extract content from RCS template structure
  const getTemplateContent = () => {
    if (!template.content) return null;

    // Plain text template
    if (template.content.plainText) {
      return {
        type: "plain",
        text: template.content.plainText,
        suggestions: template.content.suggestions || [],
      };
    }

    // Rich card template
    if (template.content.richCardDetails) {
      const richCard = template.content.richCardDetails;

      if (richCard.standalone) {
        return {
          type: "standalone",
          card: richCard.standalone.content,
          orientation: richCard.standalone.cardOrientation,
          suggestions: richCard.standalone.content?.suggestions || [],
        };
      }

      if (richCard.carousel) {
        return {
          type: "carousel",
          cards: richCard.carousel.contents || [],
          suggestions: richCard.carousel.suggestions || [],
        };
      }
    }

    return null;
  };

  const content = getTemplateContent();

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {template.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<LanguageIcon />}
              label={`Language: ${template.language}`}
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<CalendarIcon />}
              label={new Date(template.createdOn).toLocaleDateString()}
              variant="outlined"
              size="small"
            />
            <TemplateStatusChip status={template.status} />
          </Box>
        </Box>
      </Box>

      {/* Message Preview */}
      <Card
        elevation={2}
        sx={{
          maxWidth: 350,
          mx: "auto",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {/* Message Header */}
        <Box sx={{ p: 2, bgcolor: "primary.main", color: "white" }}>
          <Typography variant="subtitle2" fontWeight="medium">
            RCS Message
          </Typography>
        </Box>
        <CardContent sx={{ p: 2 }}>
          {content ? (
            <>
              {/* Plain Text Content */}
              {content.type === "plain" && (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  {content.text}
                </Typography>
              )}

              {/* Standalone Card */}
              {content.type === "standalone" && content.card && (
                <Box sx={{ mb: 2 }}>
                  {content.card.cardMedia?.contentInfo?.fileUrl && (
                    <Box
                      component="img"
                      src={content.card.cardMedia.contentInfo.fileUrl}
                      alt={content.card.cardTitle}
                      sx={{
                        width: "100%",
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                  )}
                  <Typography
                    variant="subtitle2"
                    fontWeight="medium"
                    gutterBottom
                  >
                    {content.card.cardTitle}
                  </Typography>
                  {content.card.cardDescription && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {content.card.cardDescription}
                    </Typography>
                  )}

                  {/* Card Suggestions */}
                  {content.card.suggestions?.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {content.card.suggestions.map((suggestion, idx) => (
                        <Chip
                          key={idx}
                          label={
                            suggestion.reply?.plainText ||
                            suggestion.action?.plainText
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            alignSelf: "flex-start",
                            borderColor: "primary.main",
                            color: "primary.main",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Carousel Cards */}
              {content.type === "carousel" && content.cards && (
                <Box sx={{ mb: 2 }}>
                  {content.cards.map((card, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        pb: 2,
                        borderBottom:
                          index < content.cards.length - 1
                            ? `1px solid ${theme.palette.divider}`
                            : "none",
                      }}
                    >
                      {card.cardMedia?.contentInfo?.fileUrl && (
                        <Box
                          component="img"
                          src={card.cardMedia.contentInfo.fileUrl}
                          alt={card.cardTitle}
                          sx={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                      )}
                      <Typography
                        variant="subtitle2"
                        fontWeight="medium"
                        gutterBottom
                      >
                        {card.cardTitle}
                      </Typography>
                      {card.cardDescription && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {card.cardDescription}
                        </Typography>
                      )}

                      {/* Card Suggestions */}
                      {card.suggestions?.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {card.suggestions.map((suggestion, idx) => (
                            <Chip
                              key={idx}
                              label={
                                suggestion.reply?.plainText ||
                                suggestion.action?.plainText
                              }
                              variant="outlined"
                              size="small"
                              sx={{
                                alignSelf: "flex-start",
                                borderColor: "primary.main",
                                color: "primary.main",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Outer Suggestions */}
              {content.suggestions?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {content.suggestions.map((suggestion, idx) => (
                      <Chip
                        key={idx}
                        label={
                          suggestion.reply?.plainText ||
                          suggestion.action?.plainText
                        }
                        variant="filled"
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No preview content available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Template Details */}
      <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
          Template Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Template ID
            </Typography>
            <Typography variant="body2">{template.id}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
              {template.status}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Customer ID
            </Typography>
            <Typography variant="body2">{template.customerId}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body2">{template.email}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Created On
            </Typography>
            <Typography variant="body2">
              {new Date(template.createdOn).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Updated On
            </Typography>
            <Typography variant="body2">
              {new Date(template.updatedOn).toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

const TemplateListSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" width={400} height={56} sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2}>
            <Skeleton variant="rectangular" height={56} />
            {[...Array(3)].map((_, index) => (
              <Box
                key={index}
                sx={{ p: 2, display: "flex", alignItems: "center" }}
              >
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ mr: 2 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: 1 }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RcsJioTemplates;
